/**
 * „Задържане на поръчка" — договорът с BE (docs/ordering.md §Поток:
 * задържане на поръчка). Изнесено от `bills.ts` (правилото за размера): тук са формите на
 * задържането и освобождаването; `bills.ts` само ги закача към `CreateBillIn`
 * и `BillElementOut`.
 *
 * В кода префиксът е `kitchenHold` / `kitchen_hold` — думите hold/release
 * вече са заети при номерата на фактурите в BE и при „задържания кадър" на
 * касата.
 */
import { api } from "./client";
import type { BillOut } from "./bills";

/**
 * Една група в `kitchenHold` на `POST /bills` (нова маса и допоръчка) —
 * редовете с еднакво време за кухнята: `minutes: null` — ръчно, докато
 * някой не я освободи; число — толкова минути (BE приема 1..240, FE
 * предлага 5/10/15/20/30). FE праща списък от групи
 * (`menu/kitchenHoldItems.ts`); всяка става отделна задържана партида, а
 * редовете извън групите тръгват веднага (всички партиди с еднакъв час). Без
 * полето поръчката тръгва веднага — тялото е байт за байт старото. BE го
 * пренебрегва за поръчка без станция и при replay от касата; касата
 * (локален режим) го отказва с 400.
 *
 * `elementIndexes` — индексите в `elements` на редовете от групата; празен
 * списък, повторен (и между групите) или несъществуващ индекс → 422. BE
 * приема и стария единичен обект без индекси (цялата поръчка).
 */
export interface KitchenHoldIn {
  minutes: number | null;
  elementIndexes?: number[];
}

/** Защо е освободена: ръчно (бутонът), по таймера, при закриване на сметката
 *  или от касата при влизане в локален режим. */
export type KitchenReleaseReason = "manual" | "timer" | "close" | "edge";

/**
 * `kitchen_hold` на елемент (партида) в документа на сметката — САМО на
 * елемент, който някога е бил задържан (останалите са байт за байт старите).
 * Задържана сега ⇔ `released_at == null` (`isKitchenHeld` в `lib/kitchenHold.ts`).
 * Времената са ISO по часовника на базата.
 */
export interface KitchenHoldOut {
  held_at: string;
  /** null при ръчно задържане (∞ — до „Пусни сега"). */
  hold_until: string | null;
  held_by: number | null;
  released_at: string | null;
  /** null — освободена от таймера/системата. */
  released_by: number | null;
  release_reason: KitchenReleaseReason | null;
  /** Решението „нова"/„добавка" за кухненския билет при освобождаването. */
  print_mode: "new" | "addition";
}

/**
 * „Пусни сега" — поръчката влиза в кухнята веднага (мониторите + кухненският
 * билет, както при нова поръчка). Без тяло; право `open_bill` + достъп до
 * сметката. 200 + документа на сметката и когато вече е освободена
 * (идемпотентно); 404 — чужд/липсващ елемент; 409 — закрита сметка; 423 —
 * облакът е в локален режим (касата не сервира пътя).
 */
export const releaseBillElement = async (
  billId: number,
  elementId: number,
): Promise<BillOut> => {
  const response = await api.post<BillOut>(
    `/bills/${billId}/elements/${elementId}/release`,
  );
  return response.data;
};

/**
 * „Промени" — новото време на задържана поръчка: `minutes: N` — влиза сама
 * след N минути ОТ СЕГА (1..240), `null` — „Ръчно" (до „Пусни сега"). „Сега"
 * е `releaseBillElement`. Право и достъп — като при освобождаването. 200 +
 * документа; 404 — чужд/липсващ елемент; 409 — закрита сметка или поръчката
 * вече е в кухнята (текстът на BE); 422 — минути извън 1..240; 423 — локален
 * режим.
 */
export const changeKitchenHold = async (
  billId: number,
  elementId: number,
  minutes: number | null,
): Promise<BillOut> => {
  const response = await api.put<BillOut>(
    `/bills/${billId}/elements/${elementId}/kitchen-hold`,
    { minutes },
  );
  return response.data;
};
